/**
 * Auth helpers.
 *
 * # Why a server-side callable for profile creation
 *
 * After `createUserWithEmailAndPassword` resolves, three things must
 * happen before the client can write to Firestore as the new user:
 *
 *   1. Firebase Auth fires `onAuthStateChanged` with the new user.
 *   2. Firebase Auth fires `onIdTokenChanged` with the new token.
 *   3. The Firestore SDK's *internal* `onIdTokenChanged` listener
 *      receives the event and updates its request pipeline so the
 *      next RPC carries the user's ID token.
 *
 * `createUserWithEmailAndPassword`'s Promise resolves before step 3.
 * Any direct Firestore write issued in the same microtask flush goes
 * out unauthenticated and is rejected with `permission-denied`.
 * Force-refreshing the token, awaiting `auth.authStateReady()`,
 * subscribing to `onIdTokenChanged`, and adding retry loops all
 * narrow the window without closing it. We observed ~100% reproduction
 * in production.
 *
 * Callable functions sidestep the race: the Functions client SDK
 * reliably attaches the auth token before sending, and the function
 * runs with Admin SDK privileges so security rules don't apply to
 * its writes. See `web/functions/user_provisioning.py` for the full
 * write-up of why.
 *
 * Sign-in (returning user) is not racy because:
 *   - `users/{uid}` already exists; no write is needed.
 *   - Snapshot subscriptions only attach via React hooks once
 *     `useAuth` reports a non-null user, which itself listens to
 *     `onAuthStateChanged` — by which time step 3 above has run.
 */

import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  getAdditionalUserInfo,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  verifyBeforeUpdateEmail,
  type User,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';

import { auth, functions, googleProvider } from './firebase';
import { fireSignupConversion, readGclid } from './ads';
import { track } from './analytics';
import { log } from './logger';

interface EnsureUserProfileArgs {
  displayName?: string;
  locale?: string;
  /** Google click id captured at ad landing (see rmv-tag.js). Persisted
   * server-side onto the new user at scaffold creation for later offline
   * conversion import. Omitted when the visitor didn't arrive from an ad. */
  gclid?: string;
}
interface EnsureUserProfileResult {
  ok: boolean;
  created: boolean;
  accountId: string | null;
}

/** Server-side scaffold creator. Idempotent — safe to call from
 * signup, Google new-user flow, and the EnsureUserProfile recovery
 * component. See `user_provisioning.py:ensure_user_profile`. */
export const ensureUserProfileCallable = httpsCallable<
  EnsureUserProfileArgs,
  EnsureUserProfileResult
>(functions, 'ensure_user_profile');

function profilePayload(user: User): EnsureUserProfileArgs {
  return {
    displayName: user.displayName ?? undefined,
    locale: navigator.language || 'en-GB',
    gclid: readGclid() ?? undefined,
  };
}

/** Idempotent post-auth-creation scaffold — the single funnel EVERY new-account
 * path uses: create the Firestore profile (carrying `gclid`), then fire the
 * single-fire signup Ads conversion + the analytics event. Reused by email
 * signup, the Google new-user flow, AND the Signup-page retry after a scaffold
 * failure, so a recovered signup never silently drops its conversion or gclid.
 * Single-fire-guarded by uid; Consent Mode withholds the conversion if ads were
 * declined. Safe to call more than once for the same user. */
export async function scaffoldNewAccount(user: User, method: 'email' | 'google'): Promise<void> {
  await ensureUserProfileCallable(profilePayload(user));
  fireSignupConversion(user.uid, user.email ?? '');
  track('sign_up', { method });
}

export async function signUpWithEmail(email: string, password: string): Promise<User> {
  const timer = log.startTimer();
  const credential = await createUserWithEmailAndPassword(auth, email, password);
  log.info('auth: user created', { uid: credential.user.uid, durationMs: timer() }, { sendToCloud: true });

  await scaffoldNewAccount(credential.user, 'email');
  log.info('auth: profile ensured', { uid: credential.user.uid, durationMs: timer() }, { sendToCloud: true });
  return credential.user;
}

export async function signInWithEmail(email: string, password: string): Promise<User> {
  const credential = await signInWithEmailAndPassword(auth, email, password);
  return credential.user;
}

/** Send Firebase Auth's password-reset email. The user clicks the
 * link, picks a new password on Firebase's hosted page, and can
 * then sign in normally. We always resolve successfully even if the
 * email isn't on file — surfacing "no account" leaks user existence. */
export async function sendPasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email);
}

/** True iff the signed-in user's identity providers include
 * email+password (vs Google-only). Email change via verifyBeforeUpdateEmail
 * needs a password reauth, which only makes sense when the user
 * has a password to begin with. */
export function hasPasswordProvider(user: User): boolean {
  return user.providerData.some((p) => p.providerId === 'password');
}

/** Start an email-change flow.
 *
 * 1. Reauthenticates the user — Firebase requires a recent login for
 *    sensitive operations. Email/password users supply their current
 *    password; Google users go through a popup.
 * 2. Calls verifyBeforeUpdateEmail. Firebase sends a verification link
 *    to the *new* address; clicking it actually applies the change to
 *    auth.currentUser.email.
 *
 * Caller is responsible for any optimistic Firestore updates (e.g.
 * writing the new email onto users/{uid}.email) since auth.email
 * won't update until the user clicks the verification link. */
export async function startEmailChange(newEmail: string, currentPassword?: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('startEmailChange: not signed in');
  if (hasPasswordProvider(user)) {
    if (!currentPassword) throw new Error('Current password required.');
    if (!user.email) throw new Error('Account has no email on file.');
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
  } else {
    await reauthenticateWithPopup(user, googleProvider);
  }
  await verifyBeforeUpdateEmail(user, newEmail);
}

export async function signInWithGoogle(): Promise<{ user: User; isNew: boolean }> {
  const credential = await signInWithPopup(auth, googleProvider);
  const additional = getAdditionalUserInfo(credential);
  const isNew = additional?.isNewUser ?? false;
  if (isNew) {
    // New Google users hit the same race as email signup; route through the same
    // scaffold funnel (profile + conversion + analytics) for the same reasons.
    await scaffoldNewAccount(credential.user, 'google');
  }
  return { user: credential.user, isNew };
}

export async function logOut(): Promise<void> {
  await signOut(auth);
}
